import React, { useRef, useState, useEffect, useCallback } from "react";
import { db } from "./firebase";
import { collection, onSnapshot } from "firebase/firestore";
import TeamNameSash from "./components/TeamNameSash";

// Helper for '1st', '2nd', etc.
const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const buildPlaceMaps = (scoresAsc) => {
  const desc = scoresAsc.slice().reverse();
  const countByScore = new Map();
  desc.forEach(([, points]) => {
    countByScore.set(points, (countByScore.get(points) || 0) + 1);
  });

  const placeByTeam = new Map();
  const tieByTeam = new Map();
  let prevScore = null;
  let prevPlace = 0;
  desc.forEach(([team, points], i) => {
    const place = points === prevScore ? prevPlace : i + 1;
    placeByTeam.set(team, place);
    tieByTeam.set(team, (countByScore.get(points) || 0) > 1);
    prevScore = points;
    prevPlace = place;
  });

  return { placeByTeam, tieByTeam };
};

// Glow for the card border
const getGlow = (rank) => {
  if (rank === 0) return "0 0 32px 8px #ffd700, 0 0 0 7px #fff8";
  if (rank === 1) return "0 0 32px 8px #c0c0c0, 0 0 0 7px #fff8";
  return "0 12px 64px #000b, 0 0 0 4px rgba(255,255,255,0.2)";
};

// Main RoundScores component
export default function RoundScores({ gameCode, roundQuestionStartIdx, questionIdx, onClose }) {

  // State
  const [answers, setAnswers] = useState([]);
  const [teamInfo, setTeamInfo] = useState({});
  const [roundScores, setRoundScores] = useState([]);

  const scrollRef = useRef(null);
  const advanceTimerRef = useRef(0);
  const rafIdRef = useRef(0);
  const { placeByTeam, tieByTeam } = React.useMemo(
    () => buildPlaceMaps(roundScores.map(({ team, points }) => [team, points])),
    [roundScores]
  );

  const titleText = "Round Scores";

  // Memoize the onClose callback - handleContinue will use the latest reference automatically
  const handleContinue = useCallback(() => {
    console.log("Roundscores: handleContinue called.");

    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = 0;
    }

    try { window.__overlayActive = false; } catch {}

    if (typeof onClose === "function") {
      console.log("Roundscores: Calling onClose prop.");
      onClose();
    } else {
      console.error("Roundscores: onClose prop is missing!");
    }
  }, [onClose]);

  // Lock auto-advance immediately on mount
  useEffect(() => {
    console.log("Roundscores: Mounted, locking overlay.");
    try { window.__overlayActive = true; } catch {}
    return () => {
        console.log("Roundscores: Unmounting, unlocking overlay.");
        // Clear timers on unmount to prevent calls after component is gone
        clearTimeout(advanceTimerRef.current);
        cancelAnimationFrame(rafIdRef.current);
        try { window.__overlayActive = false; } catch {}
    };
  }, []);

  // ArrowRight listener
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        handleContinue();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleContinue]); // Keep handleContinue here, it's stable due to useCallback

  // --- Data Fetching ---
  useEffect(() => {
    if (!gameCode) return;
    console.log("Roundscores: Subscribing to answers...");
    const unsub = onSnapshot(collection(db, "games", gameCode, "answers"), (snap) => {
      const all = [];
      snap.forEach((doc) => {
        const data = doc.data();
        if (typeof data.points === "number" && typeof data.q === "number") { all.push(data); }
      });
      setAnswers(all);
      console.log("Roundscores: Answers updated, count:", all.length);
    });
    return () => { console.log("Roundscores: Unsubscribing from answers."); unsub(); };
  }, [gameCode]);

  useEffect(() => {
    if (!gameCode) return;
    console.log("Roundscores: Subscribing to teams...");
    const unsub = onSnapshot(collection(db, "games", gameCode, "teams"), (snap) => {
      const info = {};
      snap.forEach((doc) => { info[doc.data().team] = doc.data().selfieUrl || null; });
      setTeamInfo(info);
      console.log("Roundscores: Teams updated, count:", Object.keys(info).length);
    });
    return () => { console.log("Roundscores: Unsubscribing from teams."); unsub(); };
  }, [gameCode]);

  // --- CORE SCORING LOGIC ---
  useEffect(() => {
    console.log("Roundscores: Calculating scores...");
    const scoreByTeam = {};
    const currentTeams = new Set(Object.keys(teamInfo));
    console.log(`RoundScores Calculation: roundStartIdx=${roundQuestionStartIdx}, questionIdx (ENDROUND)=${questionIdx}`);
    const relevantAnswers = answers.filter(a => a.q >= roundQuestionStartIdx && a.q < questionIdx);
    console.log(`RoundScores Calculation: Found ${relevantAnswers.length} relevant answers.`);

    relevantAnswers.forEach((a) => {
      if (!currentTeams.has(a.team)) return;
      scoreByTeam[a.team] = (scoreByTeam[a.team] || 0) + a.points;
    });

    const scored = Object.entries(scoreByTeam)
      .filter(([, score]) => !isNaN(score))
      //.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])) // Descending score
      .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0])) // Ascending score
      .map(([team, points]) => ({ team, points }));

    // Only update state if scores actually changed (using JSON compare)
    setRoundScores(prevScores => {
      if (JSON.stringify(prevScores) !== JSON.stringify(scored)) {
        console.log("Calculated Round Scores (Updating State):", scored);
        return scored;
      }
      console.log("Calculated Round Scores (No Change)");
      return prevScores;
    });

  }, [answers, teamInfo, roundQuestionStartIdx, questionIdx]);

  // --- Animation useEffect ---
  useEffect(() => {
    if (roundScores.length === 0 || !scrollRef.current) {
      console.log("Roundscores Animation: Skipping, no scores or ref not ready.");
      return;
    }

    // Lock overlay (might be redundant, but safe)
    try { window.__overlayActive = true; } catch {}
    console.log("Roundscores: Starting animation setup...");

    clearTimeout(advanceTimerRef.current);
    cancelAnimationFrame(rafIdRef.current);

    const scrollEl = scrollRef.current;
    const containerEl = scrollEl.parentElement;

    rafIdRef.current = requestAnimationFrame(() => {
      // Add slight delay before measuring
      setTimeout(() => {
          if (!containerEl || !scrollEl) { console.error("Roundscores Anim Delay: Missing elements"); return; }
          const containerWidth = containerEl.clientWidth;
          const cardEl = scrollEl.querySelector('.scroll-row > div:first-child');
          if (!cardEl) { console.error("Roundscores Anim Delay: Missing card element"); return; }

          const cardWidth = cardEl.clientWidth;
          const cardGap = 0.02 * containerWidth;
          const contentWidth = roundScores.length * (cardWidth + cardGap);
          console.log(`Roundscores Animation (Delayed): containerWidth=${containerWidth}, cardWidth=${cardWidth}, cardGap=${cardGap.toFixed(2)}, contentWidth=${contentWidth.toFixed(2)}`);

          // Guard: Content fits statically
          if (contentWidth <= containerWidth || cardWidth <= 0) { // Added cardWidth check
            console.log(`Roundscores Animation: Content fits or invalid cardWidth (${cardWidth}), applying static transform.`);
            scrollEl.style.transform = 'translateX(0)';
            scrollEl.style.animation = 'none';
            const VIEWING_DELAY_SECS_STATIC = 6;
            // Use direct handleContinue reference inside timeout
            advanceTimerRef.current = setTimeout(handleContinue, VIEWING_DELAY_SECS_STATIC * 1000);
            return;
          }

          // Proceed with animation
          const DURATION_PER_CARD_S = 1.2;
          const totalAnimationDuration = Math.max(8.0, roundScores.length * DURATION_PER_CARD_S);
          const animationName = `round-scores-pass-${gameCode}-${questionIdx}`;
          const styleId = `style-${animationName}`;
          const startTransform = "translateX(100vw)";
          const endTransform = `translateX(calc(${-(contentWidth - containerWidth + cardGap * 1).toFixed(2)}px))`;
          console.log(`Roundscores Animation: startTransform='${startTransform}', endTransform='${endTransform}', duration=${totalAnimationDuration.toFixed(2)}s`);

          const styleTag = document.getElementById(styleId) || document.createElement("style");
          styleTag.id = styleId;
          styleTag.innerHTML = `@keyframes ${animationName} { 0% { transform: ${startTransform}; } 100% { transform: ${endTransform}; } }`;
          if (!document.getElementById(styleId)) { document.head.appendChild(styleTag); }

          scrollEl.style.transform = '';
          //scrollEl.style.animation = `${animationName} ${totalAnimationDuration.toFixed(2)}s linear forwards`;
          scrollEl.style.animation = `${animationName} ${totalAnimationDuration.toFixed(2)}s ease-out forwards`;
          console.log(`Roundscores Animation: Applied animation: ${scrollEl.style.animation}`);

          const VIEWING_DELAY_SECS = 4;
          // Use direct handleContinue reference inside timeout
          advanceTimerRef.current = setTimeout(handleContinue, (totalAnimationDuration + VIEWING_DELAY_SECS) * 1000);

      }, 50); // 50ms measurement delay

    }); // End requestAnimationFrame

    // Cleanup for animation effect
    return () => {
      console.log("Roundscores: Cleaning up animation effect.");
      clearTimeout(advanceTimerRef.current);
      cancelAnimationFrame(rafIdRef.current);
      if (scrollRef.current) scrollRef.current.style.animation = 'none'; // Use ref directly
      // Unlock overlay only when animation effect cleans up
      try { window.__overlayActive = false; } catch {}
    };
  // *** REMOVED handleContinue FROM DEPENDENCY ARRAY ***
  }, [roundScores, questionIdx, gameCode]); // Depends on calculated scores and identifiers


  // --- Constants and JSX ---
  const CARD_WIDTH = "18vw";
  const CARD_HEIGHT = "80vh";
  const CARD_GAP = "2vw";

 // console.log("Roundscores: Rendering JSX, scores count:", roundScores.length);

  // Added check: Render only if scores have been calculated
  if (roundScores.length === 0) {
     console.log("Roundscores: No scores calculated yet, rendering minimal placeholder.");
     // Render minimal structure or null to avoid errors during initial render/measurement attempts
     return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, pointerEvents: 'none' }}>
           {/* Optionally, a simple loading indicator or just null */}
        </div>
     );
  }

  return (
    <div style={{ width: "100vw", height: "100vh", background: "transparent", color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "sans-serif", position: "fixed", inset: 0, overflow: "hidden", zIndex: 99999, pointerEvents: "none" }}>
      {/* Heading */}
      <div style={{ backgroundColor: "rgba(0, 0, 0, 0.8)", backdropFilter: "blur(8px)", border: "3px solid #fff", borderRadius: "1.2em", padding: "0.1em", marginTop: 8, marginBottom: 8, width: "90%", maxWidth: 800, fontSize: "3rem", fontWeight: 700, color: "#fff", textAlign: "center", boxShadow: "0 2px 18px 0 #0008", pointerEvents: "auto" }}>
        {titleText}
      </div>

      {/* Marquee/Scroll Area */}
     <div style={{ width: "100vw", flex: 1, height: "100%", position: "relative", display: "flex", alignItems: "center", pointerEvents: "auto" }}>
        <div className="scroll-row" ref={scrollRef} style={{ height: CARD_HEIGHT, display: "flex", flexDirection: "row", alignItems: "stretch", gap: CARD_GAP }}>
          {roundScores.map((a, idx) => {
            const selfieUrl = teamInfo[a.team];
            // Rank based on ASCENDING sort order (0=last, N-1=first)
            const rank = roundScores.length - 1 - idx; // 0 is leader
            const place = placeByTeam.get(a.team) ?? rank + 1;
            const isTied = tieByTeam.get(a.team) ?? false;
            const medalRank = Math.max(0, place - 1);

            return (
              <div key={a.team} style={{ height: "100%", aspectRatio: "2/3", width: CARD_WIDTH, minWidth: CARD_WIDTH, maxWidth: CARD_WIDTH, borderRadius: "2.2em", background: "rgba(0,0,0,0.80)",
                                          boxShadow: getGlow(medalRank), // Glow based on calculated rank
                                          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", position: "relative", overflow: "visible" }}>

                {/* --- Rank Badge --- */}
                <div style={{ position: "absolute", top: -85, left: "50%", transform: "translateX(-50%)", zIndex: 100, width: 170, height: 170, borderRadius: "50%", background: medalRank === 0 ? "radial-gradient(circle at 38% 32%, #fffbe9 10%, #ffe066 40%, #ffd700 70%, #b8860b 100%)" : medalRank === 1 ? "radial-gradient(circle at 38% 32%, #fff 18%, #dbdbdb 60%, #c0c0c0 90%)" : medalRank === 2 ? "radial-gradient(circle at 40% 36%, #fdf6e3 10%, #e6b98a 40%, #b87333 74%, #61320b 100%)" : "radial-gradient(circle, #f8f9fa 20%, #cccccc99 100%)", border: medalRank === 0 ? "8px solid #ffd700" : medalRank === 1 ? "8px solid #c0c0c0" : medalRank === 2 ? "8px solid #b67a3a" : "8px solid #bbb9", boxShadow: "0 18px 36px #0007, 0 0 0 14px #fff5 inset, 0 8px 30px #ffe77b99, 0 -2px 20px #fff9 inset", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 54, color: "#2d2207", fontFamily: "'Rubik', 'Segoe UI', 'Arial Black', sans-serif", overflow: "visible" }}>
                  {/* Gloss highlights */}
                  <div style={{ position: "absolute", top: 34, left: 42, width: 78, height: 18, background: "linear-gradient(90deg, #fff9 20%, #fff1 90%)", borderRadius: "24px 24px 40px 40px", opacity: 0.7, pointerEvents: "none", filter: "blur(1px)", zIndex: 2 }} />
                  <div style={{ position: "absolute", top: 88, left: 60, width: 46, height: 14, background: "linear-gradient(90deg, #fff9 50%, #fff1 100%)", borderRadius: "14px 30px 18px 24px", opacity: 0.4, pointerEvents: "none", filter: "blur(0.7px)", zIndex: 2 }} />
                  <div style={{ position: "absolute", bottom: 24, left: 22, width: 120, height: 32, background: "radial-gradient(ellipse at center, #d7c13766 55%, transparent 100%)", borderRadius: "50%", opacity: 0.47, pointerEvents: "none", filter: "blur(2.5px)" }} />
                  {/* Rank Text */}
                  <span style={{ fontSize: "3.2rem", fontWeight: 900 }}>
                    {ordinal(place)}{isTied ? "=" : ""} {/* Display rank */}
                  </span>
                </div>

                {/* --- Confetti for Leader --- */}
                {place === 1 && (
                  <div className="confetti-container">
                    {Array.from({ length: 18 }).map((_, i) => (
                      <div key={i} className={`confetti confetti${i % 6}`} />
                    ))}
                  </div>
                )}

                <TeamNameSash
                  teamName={a.team}
                  place={place}
                  selfieUrl={selfieUrl}
                  containerStyle={{ height: "70%", flexShrink: 0 }}
                />
                {/* Points */}
                <div style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", marginTop: "auto", marginBottom: 20 }}>
                  <div style={{ minWidth: 120, minHeight: 70, background: "linear-gradient(90deg, #fffbe2 5%, #ffe066 80%)", border: "5px solid #ffe066", borderRadius: "1.8em", boxShadow: "0 6px 24px #ffd700aa", color: "#8e7600", fontWeight: 900, fontSize: "3.6rem", textAlign: "center", padding: "0.3em 1em", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {typeof a.points === "number" ? a.points : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        /* Default keyframe, replaced dynamically */
        @keyframes round-scores-pass-default { 0% { transform: translateX(100vw); } 100% { transform: translateX(-100vw); } }
        .scroll-row { will-change: transform; }
        @media (max-width: 1000px) {
          .scroll-row > div { width: 200px !important; min-width: 200px !important; max-width: 200px !important; height: 320px !important; }
          .scroll-row > div > div:first-child { height: 160px !important; } /* Adjust selfie height for badge */
          .scroll-row > div > div:nth-child(2), .scroll-row > div > div:nth-child(3) { font-size: 1.2rem !important; }
        }
        /* Badge Pulse (Optional) */
        @keyframes badge-pulse { 0% { box-shadow: 0 0 32px 8px #ffd70088; } 100% { box-shadow: 0 0 48px 18px #ffd700cc; } }
        /* Confetti */
        @keyframes confetti-fall { 0% { opacity: 1; transform: translateY(0px) rotateZ(0deg); } 100% { opacity: 0; transform: translateY(85vh) rotateZ(360deg); } }
        
        .confetti-container { position: absolute; left: 0; top: 0; width: 100%; height: 100%; pointer-events: none; z-index: 99; }
        .confetti { position: absolute; width: 12px; height: 18px; border-radius: 2px; opacity: 0.8; top: -20px; animation: confetti-fall 1.3s linear infinite; }

        .confetti0 { left: 12%; background: #ffd700; animation-delay: .0s;}
        .confetti1 { left: 24%; background: #e74c3c; animation-delay: .2s;}
        .confetti2 { left: 37%; background: #00e4d7; animation-delay: .35s;}
        .confetti3 { left: 48%; background: #8e44ad; animation-delay: .13s;}
        .confetti4 { left: 66%; background: #ff55ff; animation-delay: .41s;}
        .confetti5 { left: 79%; background: #ff9800; animation-delay: .15s;}
      `}</style>
    </div>
  );
}